import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Modal, Spin, DatePicker, Switch, Select, Button, Tooltip } from 'antd';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import moment from 'moment';
import { get as lodashGet } from 'lodash';

import FormField, {
  FormFieldOverrideOnChange,
  FormFieldOverrideMomentOnChange,
} from '../helpers/FormField';
import { Uploader } from '../helpers/Components';
import FormSection from '../helpers/FormSection';

import { CompanySelectDropDown, SiteSelectMultiDropdown } from '../helpers/drop-downs-for-enums';

import { useDispatch, useSelector } from 'react-redux';
import {
  fetchUserById,
  getEditingUser,
  getEditingUserLoading,
  UsersAction,
  deleteUserById,
  addNewUser,
} from '../../../features/user.store';

import eng from '../../../utils/eng';
import { ROLE_TYPES } from './Users';

const UserEdit = ({ id, fullname, disabled, roles = [], onUserChanged }) => {
  const [showEditUser, setShowEditUser] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState({ showModal: false, name: '' });
  const [uploadFileList, setUploadFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);

  const dispatch = useDispatch();
  const formRef = useRef();
  const imageUploadRef = useRef();

  const loading = useSelector(getEditingUserLoading);
  const user = useSelector(getEditingUser);

  const roleOptions = useMemo(() => {
    return roles.map((r) => ({
      label: `${r.name}`,
      value: r.id,
      meta: r,
    }));
  }, [roles]);

  const getRoleById = (roleId) => roles.find((r) => String(r.id) === String(roleId));

  const generateCertificationNumber = () =>
    Math.floor(1000000000 + Math.random() * 9000000000).toString();

  const clearPreview = () => {
    if (previewImage && previewImage.startsWith('blob:')) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewImage(null);
  };

  useEffect(() => {
    if (id && showEditUser) {
      dispatch(fetchUserById({ id }));
    }
  }, [id, showEditUser, dispatch]);

  useEffect(() => {
    if (user?.profilePictureURL && showEditUser && uploadFileList.length === 0) {
      setPreviewImage(user.profilePictureURL);
    }
  }, [user, showEditUser, uploadFileList.length]);

  useEffect(() => {
    return () => {
      if (previewImage && previewImage.startsWith('blob:')) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  const closeEditUserModal = () => {
    setShowEditUser(false);
    setUploadFileList([]);
    clearPreview();
  };

  const closeDeleteUserModal = () => setShowDeleteModal({ showModal: false, name: '' });

  const handleOK = () => {
    formRef.current?.submitForm();
  };

  const handleOpenEdit = (e) => {
    e?.stopPropagation?.();
    if (!disabled) setShowEditUser(true);
  };

  const handleOpenDelete = (e) => {
    e?.stopPropagation?.();
    if (!disabled) {
      setShowDeleteModal({
        showModal: true,
        name: fullname,
      });
    }
  };

  const handleSave = async (values) => {
    try {
      const selectedRole = getRoleById(values.roleId);

      if (!selectedRole) {
        toast.error('Role is required');
        return;
      }

      const image = uploadFileList?.[0]?.originFileObj || null;

      let formValue = {
        ...values,
        certificationNumber: generateCertificationNumber(),
        isLocked: !values.enabled,
        certificationExpiry: values.certificationExpiry?.valueOf?.() || moment().valueOf(),
        role: {
          id: values.roleId,
        },
      };

      delete formValue.roleId;

      if (selectedRole.type === ROLE_TYPES.SITE) {
        formValue.assignedSiteIds = (values.assignedSiteIds || []).map((x) =>
          Number.parseInt(x, 10),
        );
      } else {
        formValue.assignedSiteIds = [];
      }

      if (selectedRole.type === ROLE_TYPES.TENANT) {
        formValue.tenantIdentifier = values.tenantIdentifier;
      }

      if (
        selectedRole.type === ROLE_TYPES.MINEARC ||
        selectedRole.type === ROLE_TYPES.SUPER_ADMIN
      ) {
        formValue.tenantIdentifier = null;
        formValue.assignedSiteIds = [];
      }

      await dispatch(addNewUser({ formValue, image, isEditing: true })).unwrap();

      toast.success('User updated successfully!');
      formRef.current?.resetForm();
      setUploadFileList([]);
      clearPreview();
      setShowEditUser(false);
      dispatch(UsersAction.setPage(0));

      if (typeof onUserChanged === 'function') {
        await onUserChanged();
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || err?.details?.message || eng.somethingWentWrong);
    }
  };

  const handleDeleteUser = async () => {
    closeDeleteUserModal();

    try {
      await dispatch(deleteUserById({ id })).unwrap();
      toast.success('User deleted successfully!');
      dispatch(UsersAction.setPage(0));

      if (typeof onUserChanged === 'function') {
        await onUserChanged();
      }
    } catch (error) {
      console.error(error);
      toast.error(lodashGet(error, 'error.details[0]', 'Delete Failed!'));
    }
  };

  let displayUser = null;
  if (user) {
    displayUser = {
      id: user.id,
      fullName: user.fullName,
      email: user.email,
      telephoneNumber: user.telephoneNumber,
      certificationExpiry: user.certificationExpiry ? moment(user.certificationExpiry) : null,
      enabled: !user.isLocked,
      roleId: user.roleId || user?.role?.id || null,
      tenantIdentifier: user.tenantIdentifier || null,
      assignedSiteIds: (user.assignedSites || []).map((s) => Number(s.id)),
    };
  }

  const YupValidation = Yup.object({
    fullName: Yup.string().required('Required'),
    email: Yup.string().required('Required').email('Invalid email address'),
    telephoneNumber: Yup.string().required('Required'),
    certificationExpiry: Yup.mixed().required('Required'),
    enabled: Yup.boolean(),
    roleId: Yup.mixed().required('Required'),

    tenantIdentifier: Yup.string().nullable().when('roleId', {
      is: (roleId) => {
        const role = roles.find((r) => String(r.id) === String(roleId));
        return role?.type === ROLE_TYPES.TENANT || role?.type === ROLE_TYPES.SITE;
      },
      then: (schema) => schema.required('Company is required'),
      otherwise: (schema) => schema.nullable(),
    }),

    assignedSiteIds: Yup.array().when('roleId', {
      is: (roleId) => {
        const role = roles.find((r) => String(r.id) === String(roleId));
        return role?.type === ROLE_TYPES.SITE;
      },
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.optional(),
    }),
  });

  return (
    <>
      <div className="users-edit-actions">
        <Tooltip title="Edit user">
          <Button
            type="text"
            className="users-icon-action"
            aria-label="Edit user"
            disabled={disabled}
            onClick={handleOpenEdit}
            icon={<img src="/icons/edit.svg" alt="" />}
          />
        </Tooltip>
        <Tooltip title="Delete user">
          <Button
            type="text"
            className="users-icon-action users-delete-action"
            aria-label="Delete user"
            disabled={disabled}
            onClick={handleOpenDelete}
            icon={<img src="/icons/delete.svg" alt="" />}
          />
        </Tooltip>
      </div>

      <Modal
        title="Edit User"
        visible={showEditUser}
        onOk={handleOK}
        onCancel={closeEditUserModal}
        width={1120}
        okButtonProps={{ disabled: loading }}
        cancelButtonProps={{ disabled: loading }}
        destroyOnClose
      >
        <FormSection>
          {displayUser ? (
            <Formik
              innerRef={formRef}
              initialValues={displayUser}
              validationSchema={YupValidation}
              validateOnBlur
              onSubmit={handleSave}
              enableReinitialize
            >
              {({ values, setFieldValue }) => {
                const selectedRole = getRoleById(values.roleId);
                const showCompany =
                  selectedRole?.type === ROLE_TYPES.TENANT || selectedRole?.type === ROLE_TYPES.SITE;
                const showSites = selectedRole?.type === ROLE_TYPES.SITE;

                return (
                  <Form>
                    <Spin spinning={loading}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                        <div>
                          <FormField name="fullName" labelText="User Name" placeholder="Input here" />
                          <FormField
                            name="email"
                            disabled
                            labelText="Email Address"
                            placeholder="Input here"
                          />
                          <FormField
                            name="telephoneNumber"
                            labelText="Telephone Number"
                            placeholder="+61"
                          />

                          <FormFieldOverrideOnChange
                            name="roleId"
                            labelText="Role Name"
                            placeholder="Select Role"
                            InputComp={(props) => (
                              <Select
                                {...props}
                                style={{ width: '100%' }}
                                options={roleOptions}
                                placeholder="Select Role"
                                onChange={(val) => {
                                  props.onChange?.(val);
                                  setFieldValue('tenantIdentifier', null);
                                  setFieldValue('assignedSiteIds', []);
                                  dispatch(UsersAction.setSelectedCompanyToTable(null));
                                }}
                              />
                            )}
                          />

                          <div style={{ marginTop: 14 }}>
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>Enabled</div>
                            <Switch
                              checked={values.enabled}
                              onChange={(checked) => setFieldValue('enabled', checked)}
                            />
                          </div>
                        </div>

                        <div>
                          <FormFieldOverrideMomentOnChange
                            name="certificationExpiry"
                            labelText="Certification Expiry"
                            InputComp={(props) => <DatePicker {...props} className="w-100" />}
                          />

                          {showCompany && (
                            <FormField
                              name="tenantIdentifier"
                              labelText="Company"
                              placeholder="Select Company"
                              InputComp={CompanySelectDropDown}
                              extraOnChange={(val) => {
                                dispatch(UsersAction.setSelectedCompanyToTable(val));
                                setFieldValue('tenantIdentifier', val);
                                setFieldValue('assignedSiteIds', []);
                              }}
                            />
                          )}

                          {showSites && (
                            <FormFieldOverrideOnChange
                              allowClear
                              mode="multiple"
                              name="assignedSiteIds"
                              labelText="Sites"
                              placeholder="Select Site(s)"
                              company={values.tenantIdentifier}
                              InputComp={(props) => (
                                <SiteSelectMultiDropdown
                                  {...props}
                                  company={values.tenantIdentifier}
                                  value={values.assignedSiteIds}
                                  onChange={(val) => {
                                    const normalized = (val || [])
                                      .map((id) => Number(id))
                                      .filter((id) => Number.isFinite(id));
                                    props.onChange?.(normalized);
                                    setFieldValue('assignedSiteIds', normalized);
                                  }}
                                />
                              )}
                            />
                          )}

                          <Uploader
                            ref={imageUploadRef}
                            className="w-200px h-200px"
                            fileList={uploadFileList}
                            onFileListChange={setUploadFileList}
                            onPreviewReady={(previewUrl) => {
                              if (previewImage && previewImage.startsWith('blob:')) {
                                URL.revokeObjectURL(previewImage);
                              }
                              setPreviewImage(previewUrl);
                            }}
                          />

                          {previewImage && (
                            <div style={{ marginTop: 12 }}>
                              <div style={{ fontWeight: 600, marginBottom: 8 }}>Preview</div>
                              <img
                                src={previewImage}
                                alt="Preview"
                                style={{
                                  width: 200,
                                  height: 200,
                                  objectFit: 'cover',
                                  borderRadius: 8,
                                  border: '1px solid #d9d9d9',
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </Spin>
                  </Form>
                );
              }}
            </Formik>
          ) : (
            <Spin spinning />
          )}
        </FormSection>
      </Modal>

      <Modal
        title="Delete User"
        visible={showDeleteModal.showModal}
        onOk={handleDeleteUser}
        onCancel={closeDeleteUserModal}
        width={400}
        destroyOnClose
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        {`Are you sure you want to delete user ${showDeleteModal.name}?`}
      </Modal>
    </>
  );
};

export default UserEdit;